import React, { useState, useEffect, useMemo } from 'react';
import { Order, OrderStatus, PaymentStatus, Company, User, PaymentRecord, Country } from '../types';
import { OrderDetail } from '../components/OrderDetail';
import { NewOrderForm } from '../components/NewOrderForm';
import { AutoTracking } from '../components/AutoTracking';
import { OrderTable } from '../components/OrderTable';
import { PaymentsLedger } from '../components/PaymentsLedger';
import { apiService } from '../services/apiService';
import { Package, Radar, Plus, Search, RefreshCw, LayoutGrid, List, DollarSign, Archive, History, Download, FileSpreadsheet } from 'lucide-react';

interface LogisticsModuleProps {
  currentUser: User;
  activeCountry: Country;
}

const LogisticsModule: React.FC<LogisticsModuleProps> = ({ currentUser, activeCountry }) => {
  const [view, setView] = useState<'LIST' | 'DETAIL' | 'NEW' | 'TRACKING' | 'PAYMENTS' | 'ARCHIVE'>('LIST');
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = currentUser.role === 'Admin';
  const canAddEdit = isAdmin || currentUser.role === 'Add/Edit';
  const canDelete = isAdmin;
  const isReadOnly = currentUser.role === 'Read Only';

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
        const data = await apiService.getOrders();
        setOrders(data || []);
    } catch (error) {
        console.error("Failed to fetch orders", error);
        setOrders([]);
    } finally {
        setIsLoading(false);
    }
  };

  // Filter orders by branch entity before applying search/status filters
  const branchOrders = useMemo(() => {
    const countryToCompany = {
      [Country.TANZANIA]: Company.SHIV_TZ,
      [Country.UGANDA]: Company.SHIV,
      [Country.KENYA]: Company.KAILASA
    };
    return orders.filter(o => o.company === countryToCompany[activeCountry]);
  }, [orders, activeCountry]);

  const handleUpdateOrder = async (updatedOrder: Order) => {
    if (isReadOnly) return;
    await apiService.saveOrder(updatedOrder);
    setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
  };

  const handleDeleteOrder = async (orderId: string) => {
    if (!canDelete) return;
    await apiService.deleteOrder(orderId);
    setOrders(prev => prev.filter(o => o.id !== orderId));
    setView('LIST');
  };

  const handleSaveOrder = async (newOrder: Order) => {
    if (isReadOnly) return;
    await apiService.saveOrder(newOrder);
    await loadOrders();
    setEditingOrder(null);
    setView('LIST');
  };

  const filteredOrders = useMemo(() => {
    return branchOrders.filter(o => {
        const matchesSearch = 
          o.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          o.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          o.vesselPlanned?.toLowerCase().includes(searchTerm.toLowerCase());

        if (view === 'ARCHIVE') return o.currentStatus === OrderStatus.CLEARED && matchesSearch;
        return o.currentStatus !== OrderStatus.CLEARED && matchesSearch;
    });
  }, [branchOrders, searchTerm, view]);

  const handleExportCSV = () => {
    if (filteredOrders.length === 0) return;

    const headers = [
      "Order ID", "Date", "Branch Entity", "Customer", "Supplier", 
      "Grades", "Total Quantity (MT)", "Contract Value (USD)", 
      "Amount Paid (USD)", "Balance (USD)", "Payment Status", "Shipping Status"
    ];

    const rows = filteredOrders.map(o => {
      const grades = (o.items || []).map(i => i.name).join('; ');
      const totalQty = (o.items || []).reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
      return [
        o.id, o.orderDate, o.company, 
        `"${o.customerName.replace(/"/g, '""')}"`,
        `"${o.supplier.replace(/"/g, '""')}"`,
        `"${grades.replace(/"/g, '""')}"`,
        totalQty, o.financials.totalAmount, o.financials.amountPaid, 
        o.financials.balanceAmount, o.financials.paymentStatus, o.currentStatus
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Nexus_Logistics_Export_${activeCountry}_${view}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const selectedOrder = orders.find(o => o.id === selectedOrderId);

  if (view === 'DETAIL' && selectedOrder) {
    return (
      <div className="p-8">
        <OrderDetail 
          order={selectedOrder} 
          onBack={() => setView(selectedOrder.currentStatus === OrderStatus.CLEARED ? 'ARCHIVE' : 'LIST')} 
          onUpdateOrder={handleUpdateOrder}
          onDeleteOrder={canDelete ? handleDeleteOrder : undefined}
        />
      </div>
    );
  }

  if (view === 'NEW') {
    return (
      <div className="p-8">
        <NewOrderForm 
          activeCompany={
            activeCountry === Country.TANZANIA ? Company.SHIV_TZ : 
            activeCountry === Country.UGANDA ? Company.SHIV : Company.KAILASA
          } 
          initialData={editingOrder || undefined}
          onSubmit={handleSaveOrder} 
          onCancel={() => { setEditingOrder(null); setView('LIST'); }} 
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-500">
      <div className="bg-white border-b border-slate-200 px-8 flex items-center justify-between sticky top-0 z-20 shrink-0 shadow-sm">
        <div className="flex gap-8 overflow-x-auto no-scrollbar">
          {[
            { id: 'LIST', label: 'Order Pipeline', icon: Package },
            { id: 'PAYMENTS', label: 'Payments Ledger', icon: DollarSign },
            { id: 'TRACKING', label: 'Live Tracking', icon: Radar },
            { id: 'ARCHIVE', label: 'Completed Orders', icon: History }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setEditingOrder(null); setView(tab.id as any); }}
              className={`py-4 text-xs font-black uppercase tracking-widest flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ${
                view === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 py-2">
           <button 
              onClick={handleExportCSV}
              disabled={filteredOrders.length === 0}
              className="px-4 py-2 text-slate-600 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2 disabled:opacity-50"
           >
              <Download size={14} /> Export Report
           </button>
           {view === 'LIST' && canAddEdit && (
             <button onClick={() => setView('NEW')} className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-indigo-700 transition-all flex items-center gap-2">
                <Plus size={14} /> New Order
             </button>
           )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        {(view === 'LIST' || view === 'ARCHIVE') && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-2xl font-black text-slate-800 tracking-tight uppercase flex items-center gap-3">
                  {view === 'ARCHIVE' ? 'Completed Orders' : 'Active Pipeline'}
                </h2>
                <p className="text-slate-500 text-sm font-medium">Monitoring {activeCountry} node supply and logistics operations.</p>
              </div>
              <div className="relative flex-1 md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input 
                  type="text" 
                  placeholder="Search by ID, Consignee, Vessel..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 outline-none shadow-sm text-sm transition-all"
                />
              </div>
            </div>
            {isLoading ? (
               <div className="py-20 flex justify-center"><RefreshCw className="animate-spin text-indigo-400" /></div>
            ) : (
              <OrderTable 
                orders={filteredOrders}
                onView={(order) => { setSelectedOrderId(order.id); setView('DETAIL'); }}
                onEdit={(order) => { setEditingOrder(order); setView('NEW'); }}
                canEdit={canAddEdit}
                canDelete={canDelete}
                onDelete={handleDeleteOrder}
              />
            )}
          </div>
        )}

        {view === 'TRACKING' && <AutoTracking orders={branchOrders} onUpdateOrder={handleUpdateOrder} />}
        {view === 'PAYMENTS' && <PaymentsLedger orders={branchOrders} onUpdateOrder={handleUpdateOrder} />}
      </div>
    </div>
  );
};

export default LogisticsModule;